import axios from 'axios';
import { setupAuthInterceptor } from '../common';
import { NAMESPACES } from '../configuration';
const BASE_URL = NAMESPACES.payments_api.cache;


const api = setupAuthInterceptor(axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
}));

/**
 * @ns.route('/FlushLastSessionByLocalId/<int:local_id>')
@ns.param('local_id', 'ID del local cuya sesión en caché se eliminará')
class FlushLastSessionByLocalId(Resource):
    @ns.doc('flush_last_session',
            description='**ELIMINA LA SESIÓN EN CACHÉ** para un local específico. '
                       'Este endpoint remueve el valor almacenado en caché como last_session_<local_id>, '
                       'forzando al sistema a consultar la base de datos en la próxima solicitud. '
                       'Útil cuando se cierra una sesión o se necesita refrescar la sesión activa.',
            responses={
                200: 'Caché eliminado exitosamente',
                500: 'Error al eliminar caché'
            })
    @jwt_required()
    def delete(self, local_id):
        '''
        Elimina la sesión en caché para un local específico.
        
        **USO:**
        Este endpoint elimina el valor en caché que almacena la última sesión activa
        para un local. Esto fuerza al sistema a consultar la base de datos en la
        próxima operación que requiera la sesión.
        
        **CUÁNDO USAR:**
        - Después de cerrar una sesión manualmente
        - Cuando se abre una nueva sesión y se necesita refrescar
        - Para debugging de problemas con sesiones en caché
        - Al cambiar de turno (shift) para forzar actualización
        
        **CACHE KEY:**
        Se elimina: `last_session_{local_id}`
        
        **EJEMPLO:**
        ```
        DELETE /api/payments_api/cache/FlushLastSessionByLocalId/1
        Authorization: Bearer <token>
        ```
        
        **RESPUESTA EXITOSA:**
        ```json
        {
          "success": true,
          "message": "Cache flushed successfully for local_id 1",
          "cache_key": "last_session_1"
        }
        ```
        
        **NOTA IMPORTANTE:**
        - Este endpoint solo elimina el caché, no afecta la base de datos
        - La próxima operación consultará LocalSession en la BD
        - El caché se volverá a crear automáticamente en la próxima consulta
        - El TTL del caché es de 10 minutos por defecto
        
        **CASOS DE USO:**
        
        1. **Cierre de Sesión:**
        ```
        POST /sessions/close/123
        DELETE /cache/FlushLastSessionByLocalId/1
        ```
        
        2. **Apertura de Nueva Sesión:**
        ```
        POST /sessions/open
        DELETE /cache/FlushLastSessionByLocalId/1
        ```
        
        3. **Cambio de Turno:**
        ```
        PUT /sessions/123/switch-shift
        DELETE /cache/FlushLastSessionByLocalId/1
        ```
        
        4. **Debugging:**
        ```
        # Si hay problemas con sesión incorrecta en caché
        DELETE /cache/FlushLastSessionByLocalId/1
        # Próxima operación consultará BD actualizada
        ```
        '''
        try:
            current_app.logger.info(f"Flushing cache for last_session_{local_id}")
            
            # Build the cache key
            cache_key = f"last_session_{local_id}"
            
            # Delete from cache
            cache.delete(cache_key)
            
            current_app.logger.info(f"Cache flushed successfully: {cache_key}")
            
            return {
                'success': True,
                'message': f'Cache flushed successfully for local_id {local_id}',
                'cache_key': cache_key
            }, 200
            
        except Exception as e:
            current_app.logger.error(f"Error flushing cache for local_id {local_id}: {str(e)}", exc_info=True)
            return {
                'success': False,
                'error': 'Failed to flush cache',
                'details': str(e)
            }, 500
 */

export const flushLastSessionByLocalId = async (localId: number) => {
 return await api.delete(`/FlushLastSessionByLocalId/${localId}`);
 }